import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import KioskView from './components/KioskView';
import WebView from './components/WebView';
import MobileView from './components/MobileView';
import { GraphService } from './services/graphService';
import { AuthService } from './services/authService';
import { getOfficeConfig } from './config/offices';
import './styles/app.css';

// Load the office config based on URL (supports multi-office deployment)
const officeConfig = getOfficeConfig();

// Set the browser tab title per office (falls back to the static index.html title)
if (officeConfig.documentTitle) {
  document.title = officeConfig.documentTitle;
}

// Apply per-office branding as CSS variables. app.css falls back to the
// original SSP values when a variable (or the whole theme) is missing,
// so offices without a theme keep the original look.
(function applyTheme() {
  const t = officeConfig.theme;
  if (!t) return;
  const root = document.documentElement;
  if (t.headerBg) root.style.setProperty('--theme-header-bg', t.headerBg);
  if (t.headerDark) root.style.setProperty('--theme-header-dark', t.headerDark);
  if (t.headerAccent) root.style.setProperty('--theme-header-accent', t.headerAccent);
  if (t.headingFont) root.style.setProperty('--theme-heading-font', t.headingFont);
  if (t.accent) root.style.setProperty('--theme-accent', t.accent);
  if (t.sectionHeading) root.style.setProperty('--theme-section-heading', t.sectionHeading);
  if (t.accent2) root.style.setProperty('--theme-accent2', t.accent2);
  if (t.buttonBg) root.style.setProperty('--theme-button-bg', t.buttonBg);
})();

// Detect mode from URL parameter or viewport
const urlParams = new URLSearchParams(window.location.search);

function detectMode() {
  const modeParam = urlParams.get('mode');
  // Explicit URL params
  if (modeParam === 'kiosk') return 'kiosk';
  if (modeParam === 'mobile') return 'mobile';
  if (modeParam === 'web') return 'web';
  // Auto-detect mobile from viewport width
  if (typeof window !== 'undefined' && window.innerWidth <= 768) return 'mobile';
  // Default to web
  return 'web';
}

const APP_MODE = detectMode();
const IS_KIOSK = APP_MODE === 'kiosk';

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

// Check if this is a direct booking link (from kiosk QR scan)
const DIRECT_BOOKING = urlParams.get('room') ? {
  roomId: urlParams.get('room'),
  date: urlParams.get('date'),
  startTime: urlParams.get('start'),
  endTime: urlParams.get('end'),
  subject: urlParams.get('subject') || '',
} : null;

export default function App() {
  const [user, setUser] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(DIRECT_BOOKING?.roomId || null);
  const [selectedDate, setSelectedDate] = useState(DIRECT_BOOKING?.date || todayStr());
  const [availability, setAvailability] = useState({});
  const [showQR, setShowQR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [existingBooking, setExistingBooking] = useState(null);
  const [error, setError] = useState(null);
  const [directBooking, setDirectBooking] = useState(DIRECT_BOOKING);

  // Track selectedDate in a ref so interval callbacks see the latest value
  const dateRef = useRef(selectedDate);
  useEffect(() => { dateRef.current = selectedDate; }, [selectedDate]);

  // Initialize auth
  useEffect(() => {
    async function init() {
      try {
        if (IS_KIOSK) {
          setLoading(false);
          await loadAvailability(selectedDate);
        } else {
          const account = await AuthService.initialize();
          if (account) {
            setUser(account);
            // If returning from a redirect with restored booking params, apply them
            if (account.restoredBooking) {
              const rb = account.restoredBooking;
              setDirectBooking(rb);
              setSelectedRoom(rb.roomId);
              if (rb.date) setSelectedDate(rb.date);
              await loadAvailability(rb.date || selectedDate);
            } else {
              await loadAvailability(selectedDate);
            }
          }
          setLoading(false);

          if (DIRECT_BOOKING && !account) {
            handleLogin();
          }
        }
      } catch (err) {
        console.error('Init error:', err);
        setLoading(false);
      }
    }
    init();
  }, []);

  // Auto-refresh availability every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => loadAvailability(dateRef.current), 60000);
    return () => clearInterval(interval);
  }, []);

  // Re-fetch when selectedDate changes
  useEffect(() => {
    loadAvailability(selectedDate);
  }, [selectedDate]);

  const loadAvailability = async (date) => {
    try {
      // All views use the Azure Function API for availability.
      // The API uses calendarView which returns organizer names;
      // the delegated getSchedule endpoint does not.
      const data = await GraphService.getAvailabilityViaApi(date);
      setAvailability(data);

      if (!IS_KIOSK && user) {
        const existing = await GraphService.getUserExistingBooking(user.username);
        setExistingBooking(existing);
      }
    } catch (err) {
      console.error('Failed to load availability:', err);
    }
  };

  const handleLogin = async () => {
    try {
      const account = await AuthService.login();
      setUser(account);
      await loadAvailability(selectedDate);
    } catch (err) {
      setError('Sign-in failed. Please try again.');
    }
  };

  const handleLogout = async () => {
    await AuthService.logout();
    setUser(null);
  };

  const handleRoomSelect = (roomId) => {
    setSelectedRoom(roomId);
    setBookingSuccess(null);
    setError(null);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedRoom(null);
    setBookingSuccess(null);
    setError(null);
  };

  // Kiosk idle timer: reset to default state after 30 seconds of no interaction
  useEffect(() => {
    if (!IS_KIOSK) return;

    let idleTimer = null;
    const IDLE_TIMEOUT = 30000; // 30 seconds

    const resetIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        setSelectedRoom(null);
        setShowQR(null);
        setSelectedDate(todayStr());
        setBookingSuccess(null);
        setError(null);
      }, IDLE_TIMEOUT);
    };

    const events = ['touchstart', 'mousedown', 'mousemove', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle(); // Start the timer

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      events.forEach(e => window.removeEventListener(e, resetIdle));
    };
  }, []);

  const handleBook = async (bookingDetails) => {
    if (IS_KIOSK) {
      setShowQR(bookingDetails);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Resolve "any available" to a specific room
      let actualRoomId = bookingDetails.roomId;
      if (bookingDetails.roomId === 'any-cubicle' || bookingDetails.roomId === 'any-office') {
        const type = bookingDetails.roomId === 'any-cubicle' ? 'cubicle' : 'office';
        const available = officeConfig.rooms.filter(r =>
          r.type === type && r.bookable !== false && !availability[r.id]?.currentlyBooked
        );
        if (available.length === 0) {
          setError(`No ${type}s available for that time slot.`);
          setLoading(false);
          return;
        }
        actualRoomId = available[0].id;
        bookingDetails.roomId = actualRoomId;
        bookingDetails.roomName = available[0].name;
      }

      // Max 2 simultaneous bookings per day (not counting cubicles).
      // This only limits how many rooms you have on the SAME day,
      // so booking future dates is always allowed.
      const MAX_SIMULTANEOUS = 2;
      const activeBookings = await GraphService.getUserActiveBookings();
      const sameDayBookings = activeBookings.filter(b => {
        if (b.roomType === 'cubicle') return false; // cubicles exempt
        const bookingStartDate = b.start?.split('T')[0];
        return bookingStartDate === bookingDetails.date;
      });
      // Only auto-cancel an existing office/cubicle booking if the new booking
      // is for the SAME day. Booking tomorrow shouldn't cancel today's desk.
      const isSameDay = existingBooking && bookingDetails.date === new Date().toISOString().split('T')[0];
      const willAutoCancel = isSameDay && (bookingDetails.roomType === 'office' || bookingDetails.roomType === 'cubicle');
      const effectiveCount = sameDayBookings.length - (willAutoCancel ? 1 : 0);
      if (effectiveCount >= MAX_SIMULTANEOUS) {
        setError(`You already have ${sameDayBookings.length} bookings on ${bookingDetails.date}. Max ${MAX_SIMULTANEOUS} per day (cubicles excluded).`);
        setLoading(false);
        return;
      }

      // If user has an existing office/cubicle booking for today, cancel it first
      if (willAutoCancel) {
        await GraphService.cancelBooking(existingBooking.eventId);
      }

      // Create the booking via Graph API
      const result = await GraphService.createBooking(bookingDetails, user);
      setBookingSuccess({
        ...bookingDetails,
        roomName: officeConfig.rooms.find(r => r.id === actualRoomId)?.name || bookingDetails.roomName,
      });
      setSelectedRoom(null);
      await loadAvailability(selectedDate);
    } catch (err) {
      setError(`Booking failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Direct booking flow (from QR scan on phone)
  // Once the user authenticates, personalize the subject and open the room panel.
  // Only runs when user first signs in with a direct booking link.
  const directBookingApplied = useRef(false);
  useEffect(() => {
    if (directBooking && user && !loading && !directBookingApplied.current) {
      directBookingApplied.current = true;
      if (!directBooking.subject || directBooking.subject === 'Meeting') {
        setDirectBooking(prev => ({
          ...prev,
          subject: `${user.name?.split(' ')[0] || user.name}'s Meeting`,
        }));
      }
      setSelectedRoom(directBooking.roomId);
    }
  }, [user, loading, directBooking]);

  // Shared props for all views
  const sharedProps = {
    officeConfig,
    availability,
    selectedRoom,
    selectedDate,
    onDateChange: handleDateChange,
    onRoomSelect: handleRoomSelect,
    onBook: handleBook,
    error,
    onDismissError: () => setError(null),
    bookingSuccess,
    onDismissSuccess: () => setBookingSuccess(null),
    directBooking,
  };

  // Loading screen (non-kiosk only)
  if (loading && !IS_KIOSK) {
    return (
      <div className="app loading-screen">
        <div className="spinner" />
        <p>Loading SSP Room Booking...</p>
      </div>
    );
  }

  // Render the appropriate view
  if (APP_MODE === 'kiosk') {
    return (
      <KioskView
        {...sharedProps}
        showQR={showQR}
        onCloseQR={() => setShowQR(null)}
      />
    );
  }

  if (APP_MODE === 'mobile') {
    return (
      <MobileView
        {...sharedProps}
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        loading={loading}
        existingBooking={existingBooking}
      />
    );
  }

  // Default: web view
  return (
    <WebView
      {...sharedProps}
      user={user}
      onLogin={handleLogin}
      onLogout={handleLogout}
      loading={loading}
      existingBooking={existingBooking}
    />
  );
}
